import React, { useEffect, useState } from "react";
import BackLink from "../components/BackLink";
import PageLayout from "../components/ui/PageLayout";
import { useApi } from "../apiHelpers";
import LogoSpinner from "../components/ui/LogoSpinner";
import EmptyState from "../components/ui/EmptyState";
import Panel from "../components/ui/Panel";
import FadeIn from "../components/ui/FadeIn";

// Star icon
const StarIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className={className}>
    <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
  </svg>
);

const Timeline = () => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  useEffect(() => {
    const formatTimelineData = (data) => {
      const sortedData = [...data].sort((a, b) => new Date(b.dateissued) - new Date(a.dateissued));
      return sortedData.map((item) => ({
        date: new Date(item.dateissued),
        dateFormatted: formatDate(item.dateissued),
        winner: item.winner,
        starQty: item.starqty,
      }));
    };

    api.get(`/goldstars`)
      .then((data) => {
        const formattedData = formatTimelineData(data);
        setTimelineData(formattedData);
      })
      .catch((error) => console.error("Error fetching timeline data:", error))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Group by year and month
  const groupedData = timelineData.reduce((acc, item) => {
    const year = item.date.getFullYear();
    const month = item.date.toLocaleString('default', { month: 'long' });
    const key = `${year}-${month}`;

    if (!acc[key]) {
      acc[key] = { year, month, items: [] };
    }
    acc[key].items.push(item);
    return acc;
  }, {});

  const groups = Object.values(groupedData);

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-2 md:pt-4">
      {/* Header */}
      <div className="mb-6">
        <BackLink to="/leaderboards" label="Leaderboard" className="mb-3" />
        <div className="flex items-center gap-3 mb-1">
          <StarIcon className="w-8 h-8 text-primary drop-shadow-[0_0_8px_rgba(255,204,51,0.6)]" />
          <h1 className="text-2xl font-bold text-white">Timeline</h1>
        </div>
        <p className="text-on-surface-variant text-sm">
          {timelineData.length} gold star awards
        </p>
      </div>

      {/* Timeline */}
      {loading ? (
        <LogoSpinner label="Loading awards" />
      ) : timelineData.length === 0 ? (
        <FadeIn key="empty"><EmptyState variant="panel">No awards yet</EmptyState></FadeIn>
      ) : (
        <FadeIn key="list" className="space-y-6">
          {groups.map((group) => (
            <div key={`${group.year}-${group.month}`}>
              {/* Month/Year Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="text-sm font-semibold text-on-surface-variant">
                  {group.month} {group.year}
                </div>
                <div className="flex-1 h-px bg-outline-variant" />
                <div className="text-xs text-on-surface-variant">
                  {group.items.length} award{group.items.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Items */}
              <div className="space-y-2 pl-2 border-l-2 border-outline-variant ml-2">
                {group.items.map((item, index) => (
                  <div
                    key={index}
                    className="relative pl-6 py-2 group"
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-[-9px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full
                      bg-gray-900 border-2 border-primary/50 group-hover:border-primary
                      group-hover:shadow-[0_0_8px_rgba(255,204,51,0.4)] transition-all" />

                    {/* Card */}
                    <Panel label={`Award to ${item.winner}`} padding="sm">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{item.winner}</p>
                          <p className="text-on-surface-variant text-xs">{item.dateFormatted}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-primary font-bold">{item.starQty}</span>
                          <StarIcon className="text-primary w-4 h-4" />
                        </div>
                      </div>
                    </Panel>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </FadeIn>
      )}
    </PageLayout>
  );
};

export default Timeline;
